"use client"

/**
 * ListPageFilters — horizontal search + filter dropdown row.
 *
 * Caller debounces `onSearch` before passing to avoid request storms.
 * Dropdowns use antd Select with allowClear; placeholder shows the
 * filter label when value is empty.
 */

import { SearchOutlined } from "@ant-design/icons"
import { Input, Select } from "antd"
import type { ReactNode } from "react"

export interface ListPageFilter {
  key: string
  label: string
  value?: string | number | null
  options: Array<{ label: ReactNode; value: string | number }>
  onChange: (value: string | number | undefined) => void
  loading?: boolean
  /** Optional width override. Defaults to 160. */
  width?: number
}

interface ListPageFiltersProps {
  searchPlaceholder?: string
  searchValue?: string
  onSearch?: (value: string) => void
  filters?: ListPageFilter[]
  /** Extra actions rendered on the right (e.g. "Export" button). */
  rightSlot?: ReactNode
}

export function ListPageFilters({
  searchPlaceholder,
  searchValue,
  onSearch,
  filters = [],
  rightSlot,
}: ListPageFiltersProps) {
  if (!onSearch && filters.length === 0 && !rightSlot) return null

  return (
    <div className="flex items-center flex-wrap gap-2 mb-3">
      {onSearch && (
        <Input
          allowClear
          placeholder={searchPlaceholder}
          prefix={<SearchOutlined style={{ color: "var(--admin-muted-fg)" }} />}
          value={searchValue}
          onChange={e => onSearch(e.target.value)}
          style={{ flex: "1 1 240px", minWidth: 200, maxWidth: 360 }}
        />
      )}
      {filters.map(f => (
        <Select
          key={f.key}
          allowClear
          placeholder={f.label}
          value={f.value === null ? undefined : f.value}
          options={f.options}
          loading={f.loading}
          onChange={(val: any) => f.onChange(val)}
          style={{ width: f.width ?? 160 }}
        />
      ))}
      {rightSlot && <div className="ml-auto">{rightSlot}</div>}
    </div>
  )
}

export default ListPageFilters
